import { Show } from '@agro1desenvolvimento/agro1-design-system/components/atoms';
import { Skeleton } from '@agro1desenvolvimento/agro1-design-system/components/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppTopbar } from '@/components/app-topbar';
import { ConfirmActionDialog } from '@/components/confirm-action-dialog';
import { ClockIcon } from '@/components/icons';
import { PageLayout } from '@/components/page-layout';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import { formatDate } from '@/lib/format';
import { ActionButton } from '@/modules/lab-shared/components/action-button';
import { NoticeCard } from '@/modules/lab-shared/components/notice-card';
import { ProducerSelect } from '@/modules/lab-shared/components/producer-select';
import {
  SampleDetailCard,
  type SampleField,
} from '@/modules/lab-shared/components/sample-detail-card';
import { SamplePhotosStrip } from '@/modules/lab-shared/components/sample-photos-strip';
import { SyncFailureNotice } from '@/modules/lab-shared/components/sync-failure-notice';
import { analysisDetailSearchSchema, toListSearch } from '@/modules/lab-shared/lib/list-search';
import { InitialAnalysisForm } from '@/modules/tetrazolium-test/components/initial-analysis-form';
import { InitialAnalysisSummary } from '@/modules/tetrazolium-test/components/initial-analysis-summary';
import { RepetitionTabs } from '@/modules/tetrazolium-test/components/repetition-tabs';
import { SeedClassification } from '@/modules/tetrazolium-test/components/seed-classification';
import { SeedEntriesTable } from '@/modules/tetrazolium-test/components/seed-entries-table';
import { TetrazoliumResultSummary } from '@/modules/tetrazolium-test/components/tetrazolium-result-summary';
import { useTetrazoliumDetailViewModel } from '@/modules/tetrazolium-test/view-models/use-tetrazolium-detail-view-model';

function average(values: number[]): number {
  if (values.length === 0) return 0;

  return Math.round(values.reduce((total, value) => total + value, 0) / values.length);
}

function TetrazoliumDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const listSearch = Route.useSearch();
  const [expanded, setExpanded] = useState(true);
  const {
    detail,
    hasProducer,
    isLoading,
    error,
    isReadOnly,
    initialAnalysisSuggestion,
    isSuggestionPending,
    isWaitingSync,
    syncTimedOut,
    grades,
    damageOptions,
    hasGradesError,
    hasDamagesError,
    classification,
    images,
    pendingAction,
    setPendingAction,
    confirmPendingAction,
    isSavingInitialAnalysis,
    saveInitialAnalysis,
    startAnalysis,
    saveResults,
    rejectRepetition,
    reactivateRepetition,
  } = useTetrazoliumDetailViewModel(id);

  const goBack = () =>
    void navigate({ to: '/seeds', search: toListSearch(listSearch, 'tetrazolio') });

  if (!hasProducer) {
    return (
      <PageLayout>
        <AppTopbar
          actions={<ProducerSelect />}
          breadcrumb={t('lab.detail.analysisTitle')}
          title={t('lab.title')}
          onTitleClick={goBack}
        />
        <p className="py-10 text-center text-sm text-muted-foreground">
          {t('lab.noProducerSelected')}
        </p>
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </PageLayout>
    );
  }

  if (error || !detail) {
    return (
      <PageLayout>
        <p className="text-destructive py-10 text-center text-sm">{t('tetrazolium.loadError')}</p>
      </PageLayout>
    );
  }

  const { initialAnalysis } = detail;

  const primaryFields: SampleField[] = [
    { label: t('lab.table.requester'), value: detail.requester },
    { label: t('tetrazolium.sample.internalId'), value: detail.internalId },
    { label: t('lab.table.species'), value: detail.species },
    { label: t('tetrazolium.sample.lot'), value: detail.lot },
    { label: t('tetrazolium.sample.harvest'), value: detail.harvest },
  ];

  const secondaryFields: SampleField[] = [
    { label: t('tetrazolium.sample.scientificName'), value: detail.scientificName },
    { label: t('tetrazolium.sample.cultivar'), value: detail.cultivar },
    { label: t('tetrazolium.sample.category'), value: detail.category },
    { label: t('tetrazolium.sample.entryDate'), value: formatDate(detail.entryDate) },
    { label: t('tetrazolium.sample.samplingDate'), value: formatDate(detail.samplingDate) },
  ];

  const activeResults = classification.repetitions.filter(({ isRejected }) => !isRejected);

  return (
    <PageLayout>
      <AppTopbar
        actions={<ProducerSelect />}
        breadcrumb={t(isReadOnly ? 'lab.detail.viewTitle' : 'lab.detail.analysisTitle')}
        title={t('lab.title')}
        onTitleClick={goBack}
      />

      <SyncFailureNotice errors={detail.syncErrors} />

      <Show when={isWaitingSync || syncTimedOut}>
        <NoticeCard
          icon={<ClockIcon className="size-6.5" />}
          message={t(isWaitingSync ? 'lab.syncWaiting' : 'lab.syncPending')}
          tone="info"
        />
      </Show>

      <SampleDetailCard
        expanded={expanded}
        photos={
          <SamplePhotosStrip
            disabled={images.isUploading}
            photos={images.images.map(({ id: imageId, url, fullsizeUrl, name }) => ({
              id: imageId,
              fullsizeUrl,
              url,
              name,
            }))}
            readOnly={isReadOnly}
            onRemove={(photoId) => images.remove(photoId)}
            onUpload={(files) => images.upload(files)}
          />
        }
        primaryFields={primaryFields}
        sampleNumber={detail.sampleId}
        secondaryFields={secondaryFields}
        status={detail.status}
        summary={
          initialAnalysis ? (
            <InitialAnalysisSummary
              data={initialAnalysis}
              readOnly={isReadOnly}
              onCancel={() => setPendingAction('cancel')}
            />
          ) : undefined
        }
        onToggle={() => setExpanded((current) => !current)}
      />

      <Show when={isReadOnly && initialAnalysis}>
        <TetrazoliumResultSummary
          notes={detail.notes ?? ''}
          repetitions={initialAnalysis?.repetitions ?? 0}
          results={activeResults.map((repetition) => ({
            number: repetition.number,
            vigor: repetition.vigor,
            viability: repetition.viability,
            enteredSeeds: repetition.entryCount,
            totalSeeds: classification.seedsPerRepetition,
          }))}
          seedsPerRepetition={classification.seedsPerRepetition}
          vigor={average(activeResults.map(({ vigor }) => vigor))}
          viability={average(activeResults.map(({ viability }) => viability))}
          onBack={goBack}
        />
      </Show>

      <Show when={!isReadOnly && !initialAnalysis && isSuggestionPending}>
        <Skeleton className="h-40 w-full" />
      </Show>

      <Show when={!isReadOnly && !initialAnalysis && !isSuggestionPending}>
        <InitialAnalysisForm
          defaultValues={initialAnalysisSuggestion}
          submitting={isSavingInitialAnalysis}
          onBack={goBack}
          onSaveDraft={(input) => saveInitialAnalysis(input, goBack)}
          onSubmit={(input) => startAnalysis(input)}
        />
      </Show>

      <Show when={!isReadOnly && !!initialAnalysis}>
        <RepetitionTabs
          current={classification.currentRepetition}
          repetitions={classification.repetitions}
          onSelect={classification.goToRepetition}
        />

        <SeedClassification
          currentRepetition={classification.currentRepetition}
          damageOptions={damageOptions}
          grades={grades}
          hasDamagesError={hasDamagesError}
          hasGradesError={hasGradesError}
          isCurrentComplete={classification.isCurrentComplete}
          isCurrentRejected={classification.isCurrentRejected}
          readOnly={isReadOnly}
          stats={classification.stats}
          onAddEntry={classification.addEntry}
          onReactivateRepetition={reactivateRepetition}
          onRejectRepetition={rejectRepetition}
        />

        <Show when={classification.entries.length > 0}>
          <SeedEntriesTable
            damageOptions={damageOptions}
            entries={classification.entries}
            grades={grades}
            isLastRepetition={classification.nextRepetition === null}
            isRepetitionComplete={classification.isCurrentComplete}
            readOnly={isReadOnly}
            onAdvance={() =>
              classification.nextRepetition === null
                ? setPendingAction('complete')
                : classification.goToRepetition(classification.nextRepetition)
            }
            onRemoveEntry={classification.removeEntry}
            onSync={() => saveResults()}
          />
        </Show>

        <div className="sticky bottom-0 z-2 -mx-loose -mb-loose flex flex-wrap items-center justify-end gap-3 bg-white px-loose py-3.5 shadow-sticky">
          <ActionButton onClick={goBack}>{t('common.back')}</ActionButton>
          <ActionButton variant="outline" onClick={() => saveResults(goBack)}>
            {t('tetrazolium.entries.saveTest')}
          </ActionButton>
        </div>
      </Show>

      <UnsavedChangesDialog when={() => !isReadOnly && classification.readHasUnsavedChanges()} />

      <ConfirmActionDialog
        description={t(
          pendingAction === 'cancel'
            ? 'tetrazolium.actions.cancelConfirmDescription'
            : 'tetrazolium.actions.completeConfirmDescription',
        )}
        destructive={pendingAction === 'cancel'}
        open={pendingAction !== null}
        title={t(
          pendingAction === 'cancel'
            ? 'tetrazolium.actions.cancelConfirmTitle'
            : 'tetrazolium.actions.completeConfirmTitle',
        )}
        onConfirm={confirmPendingAction}
        onOpenChange={(open) => !open && setPendingAction(null)}
      />
    </PageLayout>
  );
}

export const Route = createFileRoute('/seeds/tetrazolium/$id')({
  validateSearch: analysisDetailSearchSchema,
  component: TetrazoliumDetailPage,
});
